import { useListContext } from 'react-admin';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { isAuthorizationError } from '@src/apis/reware/utils';

/**
 * What the Inventory list renders instead of a datagrid when the query fails.
 *
 * Without an `error` node react-admin renders the children anyway, so a failed
 * query shows an empty datagrid behind a transient toast — indistinguishable
 * from a partner with no inventory, and on a page whose whole purpose is
 * filtering, indistinguishable from a filter that matched nothing.
 *
 * The denial case is separated because it's reachable by configuration rather
 * than breakage: the sidebar entry is gated on `config_portal_app_catalog`
 * while the server gates the data separately.
 */
const InventoryItemListError = () => {
  const { error } = useListContext();

  if (isAuthorizationError(error)) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          <AlertTitle>You don&apos;t have access to inventory</AlertTitle>
          <Typography variant="body2">
            Your account can reach this page but isn&apos;t permitted to read inventory data for
            this partner. Ask your Trove representative for access, or switch to a partner where
            you already have it.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Alert severity="error">
        <AlertTitle>Couldn&apos;t load inventory</AlertTitle>
        <Typography variant="body2">
          Something went wrong fetching inventory items. Try again in a moment — if it keeps
          happening, contact your Trove representative.
        </Typography>
      </Alert>
    </Box>
  );
};

export default InventoryItemListError;
